import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import HighchartsReact from 'highcharts-react-official';
import { find, forEach, assign, isArray, isUndefined, isEmpty, includes } from 'lodash-es';
import { zonesForPartialPeriod, decorateFilledSeriesExport } from '../../utils/formatter';

import chartTypes from './ChartConfig';

class SmartChart extends PureComponent {
  static propTypes = {
    series: PropTypes.object.isRequired,
    metaSeriesData: PropTypes.object.isRequired,
    categories: PropTypes.array,
    config: PropTypes.object,
    chartId: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
    immutable: PropTypes.bool,
    allowChartUpdate: PropTypes.bool,
    filters: PropTypes.shape({
      granularity: PropTypes.string,
      period_from_date: PropTypes.string,
      period_to_date: PropTypes.string,
    }),
    type: PropTypes.oneOf(['chart', 'stockChart', 'mapChart']),
    updateArgs: PropTypes.arrayOf(PropTypes.bool),
    allowPartialPeriods: PropTypes.bool,
    skipPartialSeries: PropTypes.arrayOf(PropTypes.string),
    dynamicPartialByLastPoint: PropTypes.bool,
    dataDrivenPartial: PropTypes.bool,
    customDecorateOneSeries: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  };

  static defaultProps = {
    type: 'chart',
    immutable: false,
    allowChartUpdate: true,
    updateArgs: [true, true, true], // [redraw, oneToOne, animation]
    allowPartialPeriods: false,
    skipPartialSeries: [],
    dynamicPartialByLastPoint: false,
  };

  onChartCreated = (chart) => {
    this.chartRef = chart;
  }

  get containerProps() {
    const { chartId, className, style } = this.props;
    return {
      id: chartId,
      className: cx(className),
      style,
    };
  }

  get chartOptions() {
    const {
      series, metaSeriesData, config, categories,
    } = this.props;

    const options = {
      ...config,
      series: this.decorateSeries(series, metaSeriesData),
    };
    if (!isUndefined(categories)) assign(options.xAxis, { categories });

    return options;
  }

  getPoint = (seriesIndex, name) =>
    find(this.chartAPIRef().series[seriesIndex].data, { name });

  decorateOneSeries = (data, id, value) => {
    const {
      allowPartialPeriods,
      filters,
      skipPartialSeries,
      dynamicPartialByLastPoint,
      dataDrivenPartial,
    } = this.props;

    const formattedData = isArray(data) ? [...data] : { ...data };
    // zonesObject needed for Line and Area
    // to be filled by custom lining for  Partial Periods
    // dataDrivenPartial check needed to ensure that the series has partials from backend data
    // dataDrivenPartial data format { categories: ['cat1', 'cat2'], series_name: [{y: Number, isPartialPeriod: bool }] }

    const checkPartial = (allowPartialPeriods && !includes(skipPartialSeries, id)) && ((!isEmpty(filters) && filters.granularity !== 'daily') || dataDrivenPartial);
    const parsedData = checkPartial
      ? decorateFilledSeriesExport(formattedData, filters, value, dynamicPartialByLastPoint, dataDrivenPartial)
      : formattedData;
    const zonesObject = (checkPartial && value.type !== 'column')
      ? {
        zoneAxis: 'x',
        zones: zonesForPartialPeriod(formattedData, value, filters, dataDrivenPartial),
      } : {
        ...(value.zones ? { zones: value.zones } : {}),
      };
    return {
      data: parsedData,
      ...value,
      id,
      ...zonesObject,
    };
  };

  decorateSeries = (series, metaData) => {
    const decoratedSeries = [];
    forEach(metaData, (value, key) => {
      if (isEmpty(series[key])) return;
      decoratedSeries.push(
        this.props.customDecorateOneSeries ?
        this.props.customDecorateOneSeries(series[key], key, value):
        this.decorateOneSeries(series[key], key, value));
    });
    return decoratedSeries;
  };

  chartAPIRef = () => this.chartRef

  render() {
    const {
      type,
      immutable,
      updateArgs,
      allowChartUpdate,
    } = this.props;
    return (
      <HighchartsReact
        constructorType={type}
        options={this.chartOptions}
        highcharts={chartTypes(type)}
        callback={this.onChartCreated}
        containerProps={this.containerProps}
        allowChartUpdate={allowChartUpdate}
        updateArgs={updateArgs}
        immutable={immutable}
      />
    );
  }
}

export default SmartChart;
